import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { recognizeInsightsScreen } from '../api/insights-screen';
import { extractShortcode } from '../../statistics/lib/parse-instagram-url';
import { fetchPaidViews } from '../api/paid-views';
import { fetchPresentationMedia } from '../api/presentation-media';
import { buildPostSlides, type SlideError } from '../lib/build-post-slides';
import {
  emptyManual,
  slidesOf,
  text,
  type ManualPost,
  type PostItem,
} from '../lib/manual-post';
import { downscaleToDataUrl } from '../lib/downscale';
import { extractLinks } from '../lib/extract-links';

/**
 * Публикации презентации: те, по которым пришла статистика, и те, которые
 * заполняют руками.
 *
 * Список один и в порядке ссылок — иначе слайды встанут не в том порядке,
 * в каком их сдают. Ручную публикацию человек не набирает с клавиатуры:
 * он вставляет скриншот статистики, цифры с него снимает модель, а поля
 * остаются открытыми для правки.
 */

export type { ManualPost, PostItem };
export { slidesOf };

export function usePresentationPosts() {
  const [items, setItemsState] = useState<PostItem[]>([]);
  // Последний набор — для пересборки: вписанное в карточки руками не теряем.
  const itemsRef = useRef<PostItem[]>([]);
  const setItems = useCallback((next: PostItem[] | ((prev: PostItem[]) => PostItem[])) => {
    setItemsState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      itemsRef.current = value;
      return value;
    });
  }, []);
  const [errors, setErrors] = useState<SlideError[]>([]);
  const [isCollecting, setIsCollecting] = useState(false);
  const [progress, setProgress] = useState('');

  const collect = useCallback(async (linksText: string): Promise<PostItem[]> => {
    const links = extractLinks(linksText);
    if (links.length === 0) {
      toast.error('Не нашлось ни одной ссылки');
      return [];
    }
    setIsCollecting(true);
    try {
      const { slides, errors: failed, missing } = await buildPostSlides(
        links,
        {
          fetchMedia: fetchPresentationMedia,
          // Перезаливка не нужна: картинку забирает наш прокси в момент сборки.
          rehostImages: async () => new Map<string, string>(),
          fetchPaidViews,
        },
        setProgress,
      );

      // Порядок слайдов — порядок ссылок, поэтому собираем по самим ссылкам.
      const bySlide = new Map(slides.map((s) => [extractShortcode(s.url) ?? s.url, s]));
      const byMissing = new Map(missing.map((m) => [m.shortcode, m]));
      const filledBefore = new Map(
        itemsRef.current.flatMap((i) => (i.kind === 'manual' ? [[i.shortcode, i.post] as const] : [])),
      );
      const next: PostItem[] = [];
      const seen = new Set<string>();
      for (const link of links) {
        const shortcode = extractShortcode(link);
        if (!shortcode || seen.has(shortcode)) continue;
        seen.add(shortcode);
        const slide = bySlide.get(shortcode);
        if (slide) {
          next.push({ kind: 'found', shortcode, slide });
          continue;
        }
        const gap = byMissing.get(shortcode);
        if (gap) {
          next.push({
            kind: 'manual',
            shortcode,
            post: filledBefore.get(shortcode) ?? emptyManual(gap.url, shortcode, gap.reason),
          });
        }
      }

      setItems(next);
      setErrors(failed);
      const manualCount = next.filter((i) => i.kind === 'manual').length;
      if (next.length === 0) toast.error('Ни по одной ссылке не удалось получить статистику');
      else if (manualCount > 0) toast.warning(`Заполнить вручную: ${manualCount}`);
      else toast.success(`Публикаций собрано: ${next.length}`);
      return next;
    } catch (err) {
      console.error('[Presentation] сбор публикаций:', err);
      toast.error(err instanceof Error ? err.message : 'Не удалось собрать публикации');
      return [];
    } finally {
      setIsCollecting(false);
      setProgress('');
    }
  }, []);

  const patchManual = useCallback((shortcode: string, patch: Partial<ManualPost>): void => {
    setItems((prev) =>
      prev.map((item) =>
        item.kind === 'manual' && item.shortcode === shortcode
          ? { ...item, post: { ...item.post, ...patch } }
          : item,
      ),
    );
  }, []);

  const remove = useCallback((shortcode: string): void => {
    setItems((prev) => prev.filter((item) => item.shortcode !== shortcode));
  }, []);

  /** Скриншот → цифры. Ответ подставляется в поля, решение за человеком. */
  const readScreen = useCallback(
    async (shortcode: string, file: File): Promise<void> => {
      patchManual(shortcode, { screen: file, isReading: true });
      try {
        const image = await downscaleToDataUrl(file);
        const data = await recognizeInsightsScreen(image);
        patchManual(shortcode, {
          badge: data.kind === 'reel' ? 'Reels' : 'Пост',
          views: text(data.views),
          reach: text(data.reach),
          likes: text(data.likes),
          comments: text(data.comments),
          saved: text(data.saved),
          shares: text(data.shares),
          reposts: text(data.reposts),
          profileVisits: text(data.profileVisits),
          follows: text(data.follows),
          avgWatchSeconds: text(data.avgWatchSeconds),
          isReading: false,
        });
        // Ник на экране статистики виден не всегда — не затираем уже набранный.
        setItems((prev) =>
          prev.map((item) =>
            item.kind === 'manual' && item.shortcode === shortcode && !item.post.username.trim() && data.username
              ? { ...item, post: { ...item.post, username: data.username } }
              : item,
          ),
        );
        toast.success('Цифры со скриншота подставлены — проверьте');
      } catch (err) {
        console.error('[Presentation] разбор скриншота:', err);
        patchManual(shortcode, { isReading: false });
        toast.error(err instanceof Error ? err.message : 'Не удалось разобрать скриншот');
      }
    },
    [patchManual],
  );

  const slides = useMemo(() => slidesOf(items), [items]);

  const manualCount = items.filter((i) => i.kind === 'manual').length;

  return {
    items,
    errors,
    isCollecting,
    progress,
    manualCount,
    slides,
    collect,
    patchManual,
    remove,
    readScreen,
  };
}
